import { FC, useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Link from "@mui/material/Link";
import Tooltip from "@mui/material/Tooltip";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useInputSubmitted } from "../../hooks/useInputSubmitted";
import { ReviewModal } from "../../pages/ReviewQueue/ReviewModal";
import { SubmissionDetailsModal } from "../SubmissionViewer";
import { useParticipantLabel } from "./useParticipantLabel";

interface InputSubmittedListProps {
	coordinatorId: string | undefined;
}

// New submissions are highlighted briefly after they first appear (AC8). Under
// polling, "new" = an instanceId not seen in a previous poll.
const HIGHLIGHT_MS = 30_000;

const formatSubmittedAt = (iso: string | null): string =>
	iso ? dayjs(iso).format("MMM DD, hh:mm A") : "";

interface Selected {
	enrollmentId: string;
	instanceId: string;
}

// SIMS-4338: the "Input Submitted" feed — the coordinator's recent participant
// submissions (newest first). Compound events show as one item with a compound
// icon; "View" opens the review modal (which expands the components). Clicking
// the participant name deep-links to their profile Daily Journey tab.
export const InputSubmittedList: FC<InputSubmittedListProps> = ({
	coordinatorId,
}) => {
	const navigate = useNavigate();
	const participantLabel = useParticipantLabel();
	const { data, isLoading } = useInputSubmitted(coordinatorId);
	const items = data?.items ?? [];

	// SIMS-7579: mirror the Participants (Daily Journey) behavior — only a
	// genuinely reviewable item (status PENDING_APPROVAL) opens the actionable
	// ReviewModal; everything else (the feed's usual SUBMITTED / COMPLETED items)
	// opens the read-only SubmissionDetailsModal, so an already-final item can't
	// fire an approve the API rejects ("already reviewed").
	const [reviewing, setReviewing] = useState<Selected | null>(null);
	const [viewing, setViewing] = useState<Selected | null>(null);
	const [highlighted, setHighlighted] = useState<string[]>([]);
	const seenRef = useRef<Set<string>>(new Set());
	const initializedRef = useRef(false);
	const timersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

	useEffect(() => {
		if (!data) {
			return;
		}
		const ids = data.items.map((i) => i.instanceId);
		// Seed the first successful load without highlighting the whole feed.
		if (!initializedRef.current) {
			ids.forEach((id) => seenRef.current.add(id));
			initializedRef.current = true;
			return;
		}
		const fresh = ids.filter((id) => !seenRef.current.has(id));
		if (fresh.length === 0) {
			return;
		}
		fresh.forEach((id) => seenRef.current.add(id));
		setHighlighted((prev) => [...prev, ...fresh]);
		const timers = timersRef.current;
		fresh.forEach((id) => {
			timers[id] = setTimeout(() => {
				setHighlighted((prev) => prev.filter((x) => x !== id));
				delete timers[id];
			}, HIGHLIGHT_MS);
		});
	}, [data]);

	// Clear any pending highlight timers on unmount. We snapshot timersRef.current
	// into a local at setup time (the mutable ref itself is intentionally omitted
	// from the deps — refs are stable, so exhaustive-deps does not require it).
	useEffect(() => {
		const timers = timersRef.current;
		return () => {
			Object.values(timers).forEach(clearTimeout);
		};
	}, []);

	if (isLoading) {
		return (
			<Typography variant="body2" color="text.secondary">
				Loading…
			</Typography>
		);
	}

	if (items.length === 0) {
		return (
			<Typography variant="body2" color="text.secondary">
				No recent submissions
			</Typography>
		);
	}

	return (
		<Box>
			<List dense disablePadding>
				{items.map((item) => (
					<ListItem
						key={item.instanceId}
						disablePadding
						sx={{
							px: 1,
							borderRadius: 1,
							transition: "background-color 0.4s ease",
							backgroundColor: highlighted.includes(item.instanceId)
								? "action.hover"
								: "transparent",
							// Inline the View action (not secondaryAction) so a long
							// participant name/CIN can't overlap it — the text truncates
							// instead. Matches the Delayed & Missed chip layout.
							alignItems: "flex-start",
							gap: 1,
						}}
					>
						<ListItemText
							sx={{ my: 0, minWidth: 0 }}
							primary={
								<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
									{item.isCompound && (
										<Tooltip title="Compound submission">
											<LayersOutlinedIcon fontSize="small" aria-hidden={true} />
										</Tooltip>
									)}
									<Link
										component="button"
										variant="body2"
										underline="hover"
										onClick={() =>
											navigate(`/participants/${item.beneficiaryId}`, {
												state: {
													dailyJourneyFocusEnrollmentId: item.enrollmentId,
												},
											})
										}
									>
										{participantLabel(item)}
									</Link>
								</Box>
							}
							secondary={
								`${item.eventName || "Submission"}` +
								`${item.submittedAt ? ` · ${formatSubmittedAt(item.submittedAt)}` : ""}`
							}
						/>
						<Button
							size="small"
							sx={{ flexShrink: 0, textTransform: "none" }}
							aria-label={`View submission for ${participantLabel(item)}`}
							onClick={() => {
								const target = {
									enrollmentId: item.enrollmentId,
									instanceId: item.instanceId,
								};
								if (item.status === "PENDING_APPROVAL") {
									setReviewing(target);
								} else {
									setViewing(target);
								}
							}}
						>
							View
						</Button>
					</ListItem>
				))}
			</List>

			{/* Read-only for finalized/submitted items (the feed's usual content). */}
			<SubmissionDetailsModal
				open={Boolean(viewing)}
				enrollmentId={viewing?.enrollmentId}
				instanceId={viewing?.instanceId}
				onClose={() => setViewing(null)}
			/>
			{/* Actionable review only for genuinely reviewable (PENDING_APPROVAL). */}
			<ReviewModal
				open={Boolean(reviewing)}
				enrollmentId={reviewing?.enrollmentId}
				instanceId={reviewing?.instanceId}
				onClose={() => setReviewing(null)}
			/>
		</Box>
	);
};

export default InputSubmittedList;
